import { useModuleCloneCurriculum } from '@/composables/queries/useModuleCloneCurriculum';
import { useModuleCurriculumMutations } from '@/composables/mutations/tertiary/useModuleCurriculumMutations';

export interface CloneModuleTimetableProps {
  programmeId: string;
  sessionId: string;
  sessions: { label: string; value: string }[];
  modules: { id: string; sequence: number; name: string }[];
}

export function useCloneModuleTimetable(props: CloneModuleTimetableProps) {

  const { curriculum, loading } = useModuleCloneCurriculum(computed(() => props.programmeId));
  const { cloneModules, cloning } = useModuleCurriculumMutations();
  const targetSessionId = ref<string | null>(null);
  const copyFeeConfiguration = ref(false);
  const targetSessions = computed(() => props.sessions.filter(s => s.value !== props.sessionId));
  const level = ref<string | null>(null);
  const levels = computed(() => [...new Set(curriculum.value.map(c => c.course_registration_level))]);
  const courseOptions = computed(() => curriculum.value.filter(c => !level.value || c.course_registration_level === level.value).map(c => ({
    id: c.id, label: `${c.course?.course_code} — ${c.course?.course_title} · Level/year ${c.course_registration_level} · Semester ${c.semester}`,
  })));
  const assignments = ref<Record<number, string[]>>({});
  const error = ref('');
  const success = ref('');
  watch(() => [props.programmeId, props.sessionId], () => {
    assignments.value = {};
    targetSessionId.value = null;
    copyFeeConfiguration.value = false;
    success.value = '';
  });
  async function submit() {
    error.value = ''; success.value = '';
    try {
      const result = await cloneModules({ input: {
        programme_id: props.programmeId, from_session_id: props.sessionId, to_session_id: targetSessionId.value,
        copy_fee_configuration: copyFeeConfiguration.value,
        assignments: props.modules.filter(m => assignments.value[m.sequence]?.length).map(m => ({ sequence: m.sequence, curriculum_ids: assignments.value[m.sequence] })),
      } });
      success.value = `Created ${result?.data?.cloneProgrammeModules?.created ?? 0} modules. Select the target session to review dates and activate them.`;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unable to clone modules.';
    }
  }

  return { targetSessionId, targetSessions, copyFeeConfiguration, level, levels, courseOptions, assignments, error, success, cloning, loading, submit };
}
